// /moodboard-smoke-test — DEV-ONLY A/B harness (Jun 15 evening).
//
// Pick one moodboard + product + choose vision/render models + choose
// how the style ref is passed. Backend deconstructs the moodboard into
// a structured "ad construction spec" (layout, product treatment, shot
// style, graphic elements, CTA, text-overlay relationship) and renders
// the chosen product per that spec.
//
// Whole point: figure out which (vision, render, ref-mode) combo
// actually transfers aesthetic to a different product. The data here
// informs what production wiring SHOULD look like.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE, getAuthHeaders } from '@/lib/api';

const IS_DEV = import.meta.env.DEV;

type Vibe = 'Premium' | 'Warm & lifestyle' | 'Playful' | 'Clean & minimal' | 'Editorial';

type Moodboard = {
  id: string;
  vibe: Vibe;
  vibe_slug: string;
  tags: string[];
  style_summary: string;
  palette_words: string;
  composition_notes: string;
  image_path: string;
  image_url: string;
  created_at: string;
  // Curation status (Jun 16 — same fields surfaced on /moodboard-builder).
  // ready_for_production = approved via the smoke-test approve flow.
  // rejected = explicitly skipped from production via /moodboard-builder.
  // Mutually exclusive server-side. Surfaced here so the picker shows
  // which library entries you've already curated at a glance.
  ready_for_production?: boolean;
  rejected?: boolean;
  approved_at?: string;
  rejected_at?: string;
  // Product-type tags (Jun 17). Empty/missing = treated as universal
  // by the picker. 'wearable' is a legacy alias that subsumes
  // apparel/footwear/accessory — surfaced in the filter logic below
  // so filtering by apparel still shows wearable-tagged tiles.
  product_types?: ProductType[];
};

// Mirrors helpers.moodboards.PRODUCT_TYPES. Keep in sync.
type ProductType =
  | 'skincare' | 'haircare' | 'apparel' | 'footwear' | 'accessory'
  | 'consumable'
  | 'tea-coffee' | 'sports-drink' | 'workout-supplement'  // Jul 6 — consumable split
  | 'tech' | 'home'
  // Jul 7 — distinct apparel / footwear / beauty / supplement sub-types (strict).
  | 'shirts' | 'pants' | 'dresses' | 'activewear' | 'coats'
  | 'sneakers' | 'boots' | 'flats' | 'heels' | 'sandals'  // Jul 20
  | 'makeup' | 'face-makeup' | 'eye-makeup' | 'lip-makeup' | 'fragrance' | 'supplement'  // Jul 28
  | 'wearable'    // ← legacy alias
  | 'universal';  // ← deprecated Jun 17 evening; no longer matches
const PRODUCT_TYPE_FILTERS: ProductType[] = [
  'skincare', 'haircare', 'apparel', 'footwear', 'accessory',
  'consumable', 'tea-coffee', 'sports-drink', 'workout-supplement',
  'tech', 'home',
  'shirts', 'pants', 'dresses', 'activewear', 'coats',
  'sneakers', 'boots', 'flats', 'heels', 'sandals',
  'makeup', 'face-makeup', 'eye-makeup', 'lip-makeup', 'fragrance', 'supplement',
];
const PRODUCT_TYPE_LABEL: Record<ProductType, string> = {
  skincare: 'Skincare',
  haircare: 'Haircare',
  apparel: 'Apparel',
  footwear: 'Footwear',
  accessory: 'Accessory',
  consumable: 'Consumable',
  'tea-coffee': 'Tea & Coffee',
  'sports-drink': 'Sports / Energy',
  'workout-supplement': 'Workout Supp.',
  tech: 'Tech',
  home: 'Home',
  shirts: 'Shirts',
  pants: 'Pants',
  dresses: 'Dresses',
  activewear: 'Activewear',
  coats: 'Coats & Jackets',
  sneakers: 'Sneakers',
  boots: 'Boots',
  flats: 'Flats',
  heels: 'Heels',
  sandals: 'Sandals',
  makeup: 'Makeup',
  'face-makeup': 'Face Makeup',
  'eye-makeup': 'Eye Makeup',
  'lip-makeup': 'Lip Makeup',
  fragrance: 'Fragrance',
  supplement: 'Vitamins & Supp.',
  wearable: 'Wearable',
  universal: 'Universal',
};
// Legacy subsumption map for the CURATOR FILTER only. `wearable` is a
// deprecated alias (0 live recipes) kept so any un-migrated wearable-tagged
// recipe still surfaces under apparel/footwear/accessory. Jul 7: `consumable`
// deliberately OMITTED here — it's a LIVE parent tag, so folding it into the
// subtype filters made "Workout Supp." show the entire consumable catalog.
// The production picker's parent→child subsume still lives in the BACKEND
// (helpers/moodboards.py); this map only controls what the curator sees.
const LEGACY_TYPE_SUBSUMES: Record<string, ProductType[]> = {
  wearable: ['apparel', 'footwear', 'accessory'],
  // Jul 20 — footwear leaves are STRICT (flats → only flats recipes); no
  // footwear umbrella in matching. 'footwear' is a display grouping only.
};
// STRICT match (Jun 17 evening v2) — mirrors backend's
// _recipe_matches_product_type. Untagged + universal-only no longer
// match when a filter is active. Curator must explicitly tag a recipe
// with target type (or legacy wearable for apparel/footwear/accessory).
function matchesProductType(rec: Moodboard, target: ProductType | null): boolean {
  if (target === null) return true;
  const types = rec.product_types ?? [];
  if (types.length === 0) return false;  // strict — untagged invisible
  if (types.includes(target)) return true;
  for (const [legacy, subsumed] of Object.entries(LEGACY_TYPE_SUBSUMES)) {
    if ((types as string[]).includes(legacy) && subsumed.includes(target)) return true;
  }
  // 'universal' in types no longer matches — curator should re-tag.
  return false;
}

type ListResponse = {
  vibes: Vibe[];
  groups: Record<Vibe, Moodboard[]>;
  total: number;
};

type SmokeResponse = {
  vision_json: Record<string, unknown>;
  vision_raw: string;
  vision_model: string;
  vision_cost: number;
  vision_latency_s: number;
  render_prompt: string;
  render_model: string;
  render_image_b64: string;
  render_cost: number;
  render_latency_s: number;
  style_ref_mode: string;
  requires_model: boolean;
  total_cost: number;
  total_latency_s: number;
};

// Shape returned by GET /api/dev/moodboard-smoke-test/approvals — the
// list view replaces the heavy render_image_b64 with a boolean so the
// payload stays small. Detail view (not built yet) would return the
// full blob.
type ApprovalLite = {
  id: string;
  approved_at: string;
  vision_model: string;
  render_model: string;
  style_ref_mode: string;
  moodboard_image_url: string | null;
  moodboard_source: string;
  product_name: string;
  vision_json: Record<string, unknown>;
  render_prompt: string;
  render_image_b64: boolean;
  notes: string;
};

const VISION_MODELS = [
  // Ordered cheap-to-expensive. Default lands on gpt-5.4-mini.
  // Routing: 5.4* + 5.2 use chat.completions; 5.5* use Responses API.
  'gemini-2.5-flash',  // $0.002 — cheapest overall
  'gpt-5.4-nano',      // $0.005 — chat.completions
  'gpt-4.1',           // $0.005
  'gemini-2.5-pro',    // $0.009
  'gpt-4o',            // $0.01
  'gpt-5.4-mini',      // $0.01  — DEFAULT, chat.completions
  'gpt-5.4',           // $0.02
  'gpt-5.2',           // $0.02 — pricing unknown, placeholder
  'gpt-5.5',           // $0.19 — Responses API + reasoning
  'gpt-5.5-pro',       // $1.15 — Responses API, hard opt-in
] as const;

// Per-model cost + latency — REAL OpenAI prices × typical call shape.
const VISION_MODEL_META: Record<(typeof VISION_MODELS)[number], { cost: string; latency: string; warn?: boolean }> = {
  'gemini-2.5-flash': { cost: '$0.002', latency: '~10s' },
  'gpt-5.4-nano':     { cost: '$0.005', latency: '~10s' },
  'gpt-4.1':          { cost: '$0.005', latency: '~10s' },
  'gemini-2.5-pro':   { cost: '$0.009', latency: '~15s' },
  'gpt-4o':           { cost: '$0.01',  latency: '~15s' },
  'gpt-5.4-mini':     { cost: '$0.01',  latency: '~15s' },
  'gpt-5.4':          { cost: '$0.02',  latency: '~20s' },
  'gpt-5.2':          { cost: '$0.02?', latency: '~20s' },
  'gpt-5.5':          { cost: '$0.19',  latency: '~30s', warn: true },
  'gpt-5.5-pro':      { cost: '$1.15',  latency: '60-180s', warn: true },
};
const RENDER_MODELS = [
  'openai/gpt-image-2',
  'openai/gpt-image-1',
  'gemini/gemini-3-pro-image',
  'gemini/gemini-3.1-flash-image',
  'ideogram/v3',
] as const;
const REF_MODES = ['image-only', 'image+text', 'text-only'] as const;
type RefMode = (typeof REF_MODES)[number];

const VIBES: Vibe[] = ['Premium', 'Warm & lifestyle', 'Playful', 'Clean & minimal', 'Editorial'];

// Mirror of backend _vibe_to_slug (dev.py) — the /status reject endpoint
// takes the vibe SLUG in its path, so keep this in exact sync.
function vibeToSlug(vibe: string): string {
  return vibe
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/ /g, '-')
    .replace(/,/g, '')
    .replace(/^-+|-+$/g, '');
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error ?? new Error('FileReader failed'));
    r.readAsDataURL(file);
  });
}

function dataUrlToB64(dataUrl: string): { mime: string; b64: string } {
  const m = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
  if (!m) throw new Error('expected base64 data url');
  return { mime: m[1], b64: m[2] };
}

export default function MoodboardSmokeTestPage() {
  if (!IS_DEV) {
    return (
      <div className="min-h-screen flex items-center justify-center text-dark-muted">
        Dev-only page.
      </div>
    );
  }

  // ---- Library ----
  const [library, setLibrary] = useState<ListResponse | null>(null);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  // Product-type filter (Jun 17). null = no filter, all shown. Picking
  // a value filters the library grid to recipes tagged for that type
  // OR 'universal' OR a legacy alias that subsumes it (matchesProductType
  // mirrors the production picker's logic so what you see here is what
  // the brand-setup worker would pick for that product type).
  const [categoryFilter, setCategoryFilter] = useState<ProductType | null>(null);
  // Approval filter (Jul 7) — narrow each category to only approved or only
  // unapproved (not-yet-reviewed) recipes so you can see + run the unapproved.
  const [approvalFilter, setApprovalFilter] = useState<'all' | 'approved' | 'unapproved'>('all');
  const [picked, setPicked] = useState<Moodboard | null>(null);

  // ---- Pasted / dropped one-off moodboard (overrides library pick) ----
  // When this is set, smoke test runs against this image instead of any
  // library moodboard. NOT saved to the GCS bucket — transient.
  const [pastedPreview, setPastedPreview] = useState<string | null>(null);
  const [pastedDrag, setPastedDrag] = useState(false);
  const pastedFileRef = useRef<HTMLInputElement | null>(null);

  // ---- Product input ----
  const [productPreview, setProductPreview] = useState<string | null>(null);
  const [productName, setProductName] = useState('the product');
  // Brand name (Jun 17 evening) — fed to the render prompt's
  // no-brand-hallucination block as the positive anchor. Without
  // this, gpt-image-2 invents wordmarks (ZARA, MANGO, etc.) for
  // apparel-shaped renders.
  const [brandName, setBrandName] = useState('');
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [scrapeError, setScrapeError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ---- Test controls ----
  const [visionModel, setVisionModel] = useState<(typeof VISION_MODELS)[number]>('gpt-5.4');
  const [renderModel, setRenderModel] = useState<(typeof RENDER_MODELS)[number]>('openai/gpt-image-2');
  const [refMode, setRefMode] = useState<RefMode>('text-only');
  // Requires model (Jun 17 evening) — when checked, vision extracts
  // a model block + render forces a model-in-frame composition. Use
  // for apparel/footwear/accessory moodboards where the product is
  // what the model wears/carries (NOT the model themselves).
  const [requiresModel, setRequiresModel] = useState(false);

  // ---- Multi-select batch render (Jun 23) ----
  // Curator can toggle into multi-select mode, pick up to SELECTION_CAP
  // moodboards, then render them all with ONE product. Each render fires
  // through the same /api/dev/moodboard-smoke-test endpoint as the single
  // path; the Semaphore(3) below bounds parallelism to respect OpenAI's
  // 5 input-images/min cap on gpt-image-2 (Gotcha #69). Review-then-
  // approve: results land in a grid; user clicks Approve on each tile
  // they want to keep, Discard on the rest.
  const SELECTION_CAP = 20;
  const BATCH_CONCURRENCY = 3;
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  type BatchRunState = {
    moodboard: Moodboard;
    vibe: string;           // display vibe (→ slug) — needed for reject
    status: 'queued' | 'running' | 'done' | 'error' | 'approved' | 'discarded';
    result?: SmokeResponse;
    error?: string;
    removeText?: string;    // "remove these props/elements" edit field
    refining?: boolean;     // a refine (edit → re-render) is in flight
  };
  const [batchRuns, setBatchRuns] = useState<Map<string, BatchRunState>>(new Map());
  const [batchRunning, setBatchRunning] = useState(false);
  const batchCancelRef = useRef(false);

  // ---- Result ----
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [result, setResult] = useState<SmokeResponse | null>(null);

  // ---- Approval ----
  const [approving, setApproving] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approvals, setApprovals] = useState<ApprovalLite[]>([]);

  // ─── Library load ─────────────────────────────────────────────────
  const loadLibrary = useCallback(async () => {
    setLibraryError(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE}/api/dev/moodboard/list`, { method: 'GET', headers });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text}`);
      }
      const data = (await resp.json()) as ListResponse;
      setLibrary(data);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setLibraryError(`List failed: ${msg}`);
    }
  }, []);
  useEffect(() => { void loadLibrary(); }, [loadLibrary]);

  // ─── Approvals load ───────────────────────────────────────────────
  const loadApprovals = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE}/api/dev/moodboard-smoke-test/approvals`, {
        method: 'GET',
        headers,
      });
      if (!resp.ok) return;
      const data = (await resp.json()) as { total: number; approvals: ApprovalLite[] };
      setApprovals(data.approvals);
    } catch {
      // Soft-fail — approvals panel just shows empty
    }
  }, []);
  useEffect(() => { void loadApprovals(); }, [loadApprovals]);

  // ─── Pasted/dropped moodboard accept ──────────────────────────────
  const acceptPastedMoodboard = useCallback(async (f: File | null) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) return;
    const url = await fileToDataUrl(f);
    setPastedPreview(url);
    setPicked(null); // pasted overrides library selection
  }, []);

  // Cmd/Ctrl+V — but only when the moodboard picker pane is focused
  // (or no input is focused). Don't steal from product paste / inputs.
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      const active = document.activeElement;
      // Skip if a form field is focused — let normal paste happen.
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT')) return;
      if (!e.clipboardData) return;
      for (const item of Array.from(e.clipboardData.items)) {
        if (item.type.startsWith('image/')) {
          const f = item.getAsFile();
          if (f) {
            e.preventDefault();
            void acceptPastedMoodboard(f);
            return;
          }
        }
      }
    }
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [acceptPastedMoodboard]);

  // ─── Product file accept ──────────────────────────────────────────
  const acceptFile = useCallback(async (f: File | null) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) { setScrapeError('Drop an image.'); return; }
    const url = await fileToDataUrl(f);
    setProductPreview(url);
    setScrapeError(null);
    if (productName === 'the product') {
      setProductName(f.name.replace(/\.[^.]+$/, '') || 'the product');
    }
  }, [productName]);

  // ─── Scrape URL → product image ───────────────────────────────────
  const handleScrape = useCallback(async () => {
    const url = scrapeUrl.trim();
    if (!url) return;
    setScraping(true);
    setScrapeError(null);
    try {
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/scrape-product`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ url }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text}`);
      }
      const data = await resp.json();
      const imgUrl = data.image_url || data.image_b64;
      if (!imgUrl) throw new Error('No image returned from scrape');
      const dataUrl = imgUrl.startsWith('data:')
        ? imgUrl
        : `data:image/jpeg;base64,${imgUrl}`;
      setProductPreview(dataUrl);
      if (data.name) setProductName(data.name);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setScrapeError(`Scrape failed: ${msg}`);
    } finally {
      setScraping(false);
    }
  }, [scrapeUrl]);

  // ─── Run the smoke test ───────────────────────────────────────────
  const handleRun = useCallback(async () => {
    if (!picked && !pastedPreview) {
      setRunError('Pick a moodboard from the library or paste/drop a fresh image.');
      return;
    }
    if (!productPreview) { setRunError('Add a product image.'); return; }
    // Guardrail — don't let a careless click on the expensive vision
    // models burn $0.50-1 per run without explicit confirmation. Cost
    // bills against reasoning tokens whether or not the response
    // actually returns useful output, so even timed-out runs are real
    // money.
    if (VISION_MODEL_META[visionModel].warn) {
      const meta = VISION_MODEL_META[visionModel];
      const ok = window.confirm(
        `${visionModel}: ${meta.cost}/call, ${meta.latency} typical.\n` +
        `Reasoning tokens bill even on timeout. Continue?`,
      );
      if (!ok) return;
    }
    setRunning(true);
    setRunError(null);
    setResult(null);
    try {
      const { mime, b64 } = dataUrlToB64(productPreview);
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      // Source priority: pasted > library. When pasted, send raw b64;
      // backend skips the URL fetch path.
      const moodboardPayload: Record<string, unknown> = pastedPreview
        ? (() => {
            const p = dataUrlToB64(pastedPreview);
            return {
              moodboard_image_b64: p.b64,
              moodboard_image_mime: p.mime,
              moodboard_image_url: null,
            };
          })()
        : { moodboard_image_url: picked?.image_url };
      const resp = await fetch(`${API_BASE}/api/dev/moodboard-smoke-test`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...moodboardPayload,
          product_image_b64: b64,
          product_mime: mime,
          product_name: productName,
          vision_model: visionModel,
          render_model: renderModel,
          style_ref_mode: refMode,
          requires_model: requiresModel,
          brand_name: brandName,
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text || resp.statusText}`);
      }
      const data = (await resp.json()) as SmokeResponse;
      setResult(data);
      setApprovalNotes(''); // reset approval notes for new result
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setRunError(`Run failed: ${msg}`);
    } finally {
      setRunning(false);
    }
  }, [picked, pastedPreview, productPreview, productName, visionModel, renderModel, refMode, requiresModel, brandName]);

  // ─── Approve ──────────────────────────────────────────────────────
  const handleApprove = useCallback(async () => {
    if (!result) return;
    setApproving(true);
    try {
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/moodboard-smoke-test/approve`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          vision_model: result.vision_model,
          render_model: result.render_model,
          style_ref_mode: result.style_ref_mode,
          moodboard_image_url: pastedPreview ? null : (picked?.image_url ?? null),
          moodboard_source: pastedPreview ? 'pasted' : 'library',
          product_name: productName,
          vision_json: result.vision_json,
          render_prompt: result.render_prompt,
          render_image_b64: result.render_image_b64,
          notes: approvalNotes,
          requires_model: result.requires_model,
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text || resp.statusText}`);
      }
      await loadApprovals();
      setApprovalNotes('');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(`Approve failed: ${msg}`);
    } finally {
      setApproving(false);
    }
  }, [result, picked, pastedPreview, productName, approvalNotes, loadApprovals]);

  const handleDeleteApproval = useCallback(async (id: string) => {
    if (!window.confirm(`Delete approval ${id}?`)) return;
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `${API_BASE}/api/dev/moodboard-smoke-test/approvals/${id}`,
        { method: 'DELETE', headers },
      );
      if (!resp.ok) throw new Error(`${resp.status}`);
      await loadApprovals();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(`Delete failed: ${msg}`);
    }
  }, [loadApprovals]);

  // ─── Multi-select batch — handlers ──────────────────────────────────
  // Selection management: tile click in selectMode toggles membership.
  // SELECTION_CAP=20 prevents runaway cost (~$0.05/run = ~$1 max batch).
  // Clearing selectMode also clears selectedIds for a clean state reset.
  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= SELECTION_CAP) {
          alert(`Selection cap is ${SELECTION_CAP} moodboards. Run this batch first, then start another.`);
          return prev;
        }
        next.add(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setBatchRuns(new Map());
  }, []);

  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    clearSelection();
  }, [clearSelection]);

  // Per-tile render — wraps the same /api/dev/moodboard-smoke-test call
  // as the single path. Returns the SmokeResponse on success; throws on
  // failure so the batch runner can stamp 'error' status. Headers shared
  // per batch (one getAuthHeaders call) — passed in to avoid re-fetching.
  const renderOneMoodboard = useCallback(async (
    moodboard: Moodboard,
    productBytes: { mime: string; b64: string },
    headers: HeadersInit,
  ): Promise<SmokeResponse> => {
    const resp = await fetch(`${API_BASE}/api/dev/moodboard-smoke-test`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        moodboard_image_url: moodboard.image_url,
        product_image_b64: productBytes.b64,
        product_mime: productBytes.mime,
        product_name: productName,
        vision_model: visionModel,
        render_model: renderModel,
        style_ref_mode: refMode,
        // Batch renders use each RECIPE's own requires_model (curator-tagged),
        // NOT the global toggle — so model recipes (worn/held apparel &
        // accessory) render WITH a model and product-only recipes (skincare,
        // etc.) render WITHOUT one. The response echoes this flag, so the
        // downstream refine + approve carry it through per-recipe.
        requires_model: moodboard.requires_model,
        brand_name: brandName,
      }),
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`${resp.status}: ${text || resp.statusText}`);
    }
    return (await resp.json()) as SmokeResponse;
  }, [productName, visionModel, renderModel, refMode, brandName]);

  // Core batch runner — renders each target through Semaphore(BATCH_CONCURRENCY),
  // streaming results into batchRuns as they finish. Cancellable mid-run;
  // queued targets that haven't started skip rather than fire. Each target
  // carries its vibe so a later Deny can hit the vibe-slug reject endpoint.
  const runBatchTargets = useCallback(async (
    targets: { m: Moodboard; vibe: string }[],
  ) => {
    if (targets.length === 0 || !productPreview) return;
    setBatchRunning(true);
    batchCancelRef.current = false;
    try {
      const productBytes = dataUrlToB64(productPreview);
      const headers = await getAuthHeaders();
      (headers as Record<string, string>)['Content-Type'] = 'application/json';

      // Seed batchRuns with 'queued' so the review list renders immediately
      // (pending tiles get a spinner placeholder).
      setBatchRuns(new Map(targets.map((t) => [
        t.m.id, { moodboard: t.m, vibe: t.vibe, status: 'queued' as const },
      ])));

      // Semaphore(BATCH_CONCURRENCY) — simple promise-pool. Each worker
      // pulls from the shared queue. asyncio.gather-equivalent in JS land.
      const queue = [...targets];
      const worker = async () => {
        while (queue.length > 0) {
          if (batchCancelRef.current) return;
          const t = queue.shift();
          if (!t) return;
          const { m, vibe } = t;
          setBatchRuns((prev) => {
            const next = new Map(prev);
            next.set(m.id, { moodboard: m, vibe, status: 'running' });
            return next;
          });
          try {
            const result = await renderOneMoodboard(m, productBytes, headers);
            if (batchCancelRef.current) return;
            setBatchRuns((prev) => {
              const next = new Map(prev);
              next.set(m.id, { moodboard: m, vibe, status: 'done', result });
              return next;
            });
          } catch (e: unknown) {
            const err = e instanceof Error ? e.message : String(e);
            setBatchRuns((prev) => {
              const next = new Map(prev);
              next.set(m.id, { moodboard: m, vibe, status: 'error', error: err });
              return next;
            });
          }
        }
      };
      await Promise.all(
        Array.from({ length: BATCH_CONCURRENCY }, () => worker()),
      );
    } finally {
      setBatchRunning(false);
    }
  }, [productPreview, renderOneMoodboard]);

  // Multi-select batch (manual tile pick).
  const runBatch = useCallback(async () => {
    if (selectedIds.size === 0 || !library) return;
    const targets: { m: Moodboard; vibe: string }[] = [];
    for (const v of VIBES) {
      for (const m of library.groups[v] ?? []) {
        if (selectedIds.has(m.id)) targets.push({ m, vibe: v });
      }
    }
    await runBatchTargets(targets);
  }, [selectedIds, library, runBatchTargets]);

  // Category batch (the primary onboarding flow) — render EVERY unapproved
  // recipe (not ready_for_production, not rejected) matching the selected
  // category, using the uploaded product. Results stream into the review
  // list where each is approved / denied / refined.
  const runUnapprovedInCategory = useCallback(async () => {
    if (!library || categoryFilter === null) return;
    const targets: { m: Moodboard; vibe: string }[] = [];
    for (const v of VIBES) {
      for (const m of library.groups[v] ?? []) {
        if (m.ready_for_production || m.rejected) continue;
        if (!matchesProductType(m, categoryFilter)) continue;
        targets.push({ m, vibe: v });
      }
    }
    await runBatchTargets(targets);
  }, [library, categoryFilter, runBatchTargets]);

  // Count of unapproved recipes in the current category (drives the
  // "Run all unapproved" button label + disabled state).
  const unapprovedInCategoryCount = useMemo(() => {
    if (!library || categoryFilter === null) return 0;
    let n = 0;
    for (const v of VIBES) {
      for (const m of library.groups[v] ?? []) {
        if (m.ready_for_production || m.rejected) continue;
        if (matchesProductType(m, categoryFilter)) n++;
      }
    }
    return n;
  }, [library, categoryFilter]);

  // Approve ONE batch result — same backend call as the single approve
  // path. Stamps the run's batchRuns entry as 'approved' so the tile
  // shows it without needing a re-fetch.
  const approveBatchOne = useCallback(async (id: string) => {
    const entry = batchRuns.get(id);
    if (!entry?.result) return;
    try {
      const headers = await getAuthHeaders();
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/moodboard-smoke-test/approve`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          vision_model: entry.result.vision_model,
          render_model: entry.result.render_model,
          style_ref_mode: entry.result.style_ref_mode,
          moodboard_image_url: entry.moodboard.image_url,
          moodboard_source: 'library',
          product_name: productName,
          vision_json: entry.result.vision_json,
          render_prompt: entry.result.render_prompt,
          render_image_b64: entry.result.render_image_b64,
          notes: '',
          requires_model: entry.result.requires_model,
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text || resp.statusText}`);
      }
      setBatchRuns((prev) => {
        const next = new Map(prev);
        const e = next.get(id);
        if (e) next.set(id, { ...e, status: 'approved' });
        return next;
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(`Approve failed for ${id}: ${msg}`);
    }
  }, [batchRuns, productName]);

  // Deny — persist a rejection (excludes this recipe from future category
  // batches + never reaches production) AND drop it from the review list.
  // Uses the /status endpoint (vibe slug + moodboard id).
  const rejectBatchOne = useCallback(async (id: string) => {
    const entry = batchRuns.get(id);
    if (!entry) return;
    setBatchRuns((prev) => {          // optimistic remove
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    try {
      const headers = await getAuthHeaders();
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
      const slug = vibeToSlug(entry.vibe);
      const resp = await fetch(
        `${API_BASE}/api/dev/moodboard/${slug}/${entry.moodboard.id}/status`,
        { method: 'POST', headers, body: JSON.stringify({ status: 'rejected' }) },
      );
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text || resp.statusText}`);
      }
      void loadLibrary();             // reflect the reject in the picker/counts
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(`Reject failed for ${entry.moodboard.name}: ${msg}`);
    }
  }, [batchRuns, loadLibrary]);

  // Bind the per-result "remove these elements" text.
  const setBatchRemoveText = useCallback((id: string, text: string) => {
    setBatchRuns((prev) => {
      const next = new Map(prev);
      const e = next.get(id);
      if (e) next.set(id, { ...e, removeText: text });
      return next;
    });
  }, []);

  // Refine — render came back too close to the moodboard (identical props);
  // the curator named elements to remove. Strip them from the recipe +
  // re-render text-only, swap the result in place so Approve saves the
  // CLEANED recipe.
  const refineBatchOne = useCallback(async (id: string) => {
    const entry = batchRuns.get(id);
    if (!entry?.result || !productPreview) return;
    const removals = (entry.removeText ?? '').trim();
    if (!removals) return;
    setBatchRuns((prev) => {
      const next = new Map(prev);
      const e = next.get(id);
      if (e) next.set(id, { ...e, refining: true });
      return next;
    });
    try {
      const headers = await getAuthHeaders();
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/moodboard-smoke-test/refine`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          vision_json: entry.result.vision_json,
          remove_instructions: removals,
          product_image_b64: dataUrlToB64(productPreview),
          product_mime: 'image/png',
          product_name: productName,
          brand_name: brandName,
          render_model: entry.result.render_model,
          requires_model: entry.result.requires_model,
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text || resp.statusText}`);
      }
      const data = (await resp.json()) as {
        vision_json: Record<string, unknown>;
        render_prompt: string;
        render_image_b64: string;
        style_ref_mode: string;
      };
      setBatchRuns((prev) => {
        const next = new Map(prev);
        const e = next.get(id);
        if (e?.result) {
          next.set(id, {
            ...e,
            refining: false,
            result: {
              ...e.result,
              vision_json: data.vision_json,
              render_prompt: data.render_prompt,
              render_image_b64: data.render_image_b64,
              style_ref_mode: data.style_ref_mode,
            },
          });
        }
        return next;
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setBatchRuns((prev) => {
        const next = new Map(prev);
        const en = next.get(id);
        if (en) next.set(id, { ...en, refining: false });
        return next;
      });
      alert(`Refine failed: ${msg}`);
    }
  }, [batchRuns, productPreview, productName, brandName]);

  const totalCount = useMemo(() => library?.total ?? 0, [library]);
  // Filtered total — when categoryFilter is set, count tiles matching
  // the filter (across all vibes). Used by the empty-state message
  // when the filter excludes everything.
  const filteredTotal = useMemo(() => {
    if (!library) return 0;
    if (categoryFilter === null) return library.total;
    return VIBES.reduce(
      (sum, v) => sum + (library.groups[v] ?? []).filter((m) => matchesProductType(m, categoryFilter)).length,
      0,
    );
  }, [library, categoryFilter]);

  return (
    <div className="min-h-screen bg-surface p-6">
      <div className="max-w-[1500px] mx-auto">
        {/* Header */}
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-dark">Moodboard smoke test</h1>
          <p className="text-sm text-dark-muted mt-1">
            dev · {totalCount} moodboards · pick one, add a product, choose models, run. Output is one render — compare against the moodboard ref to see if aesthetic actually transferred.
          </p>
        </header>

        {/* Top: picker + controls */}
        <section className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6 mb-8">
          {/* LEFT — Moodboard picker */}
          <div className="bg-white rounded-2xl border border-surface-dark/40 p-5">
            <div className="flex items-center justify-between mb-3 gap-3">
              <h2 className="font-semibold text-dark">
                1 — Pick {selectMode ? 'moodboards' : 'a moodboard'}
                {selectMode && selectedIds.size > 0 && (
                  <span className="ml-2 text-xs text-primary font-bold">
                    {selectedIds.size}/{SELECTION_CAP}
                  </span>
                )}
              </h2>
              <div className="flex items-center gap-2">
                {/* Multi-select toggle (Jun 23) — flips tile click from
                    pick-one to toggle-many. Cap is SELECTION_CAP. Exiting
                    select mode clears the selection + any batch results. */}
                <button
                  type="button"
                  onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                    selectMode
                      ? 'bg-primary text-white'
                      : 'bg-surface text-dark-muted hover:bg-surface-dark/40 hover:text-dark'
                  }`}
                  title={selectMode ? 'Exit multi-select' : `Pick up to ${SELECTION_CAP} moodboards and render them in one batch`}
                >
                  {selectMode ? '✓ Multi-select' : 'Multi-select'}
                </button>
                {selectMode && selectedIds.size > 0 && (
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="text-xs text-dark-muted hover:text-dark"
                  >Clear</button>
                )}
                <button
                  type="button"
                  onClick={() => void loadLibrary()}
                  className="text-xs text-dark-muted hover:text-dark"
                >↻ Refresh</button>
              </div>
            </div>

            {/* Pasted/dropped one-off moodboard — overrides library pick.
                Goes ABOVE the library so it's the first thing visible. */}
            <div
              onDragOver={(e) => { e.preventDefault(); setPastedDrag(true); }}
              onDragLeave={() => setPastedDrag(false)}
              onDrop={(e) => {
                e.preventDefault();
                setPastedDrag(false);
                const f = e.dataTransfer.files?.[0];
                if (f) void acceptPastedMoodboard(f);
              }}
              className={`mb-4 rounded-xl border-2 border-dashed transition-colors ${
                pastedDrag ? 'border-primary bg-primary-light/20' : 'border-surface-dark/50'
              }`}
            >
              {pastedPreview ? (
                <div className="flex items-center gap-3 p-3">
                  <img src={pastedPreview} alt="pasted moodboard" className="w-20 h-20 object-cover rounded-lg border border-surface-dark/40" />
                  <div className="flex-1 text-xs">
                    <div className="font-semibold text-dark">Pasted moodboard (one-off)</div>
                    <div className="text-dark-muted/80 mt-1">This image is used for THIS test only — not saved to the library. To curate, use /moodboard-builder.</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPastedPreview(null)}
                    className="px-2.5 py-1.5 rounded-md text-xs text-dark-muted hover:text-red-600 hover:bg-red-50"
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <div className="p-4 text-center">
                  <div className="text-xs text-dark-muted mb-2">
                    Or test a one-off image: drop here · paste (⌘V anywhere) · {' '}
                    <button
                      type="button"
                      onClick={() => pastedFileRef.current?.click()}
                      className="underline hover:text-dark"
                    >browse</button>
                  </div>
                  <input
                    ref={pastedFileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void acceptPastedMoodboard(f); e.target.value = ''; }}
                  />
                </div>
              )}
            </div>

            {/* Product-type filter (Jun 17). Picks one category and the
                library grid below trims to matching tiles. Mirrors the
                production picker's matchesProductType logic so what you
                see here is what the brand-setup worker would pick for
                that product type. */}
            <div className="mb-3 flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-dark-muted mr-1">
                Filter by category:
              </span>
              <button
                type="button"
                onClick={() => setCategoryFilter(null)}
                className={`px-2 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                  categoryFilter === null
                    ? 'bg-primary text-white'
                    : 'bg-surface text-dark-muted hover:bg-surface-dark/40 hover:text-dark'
                }`}
              >
                All
              </button>
              {PRODUCT_TYPE_FILTERS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setCategoryFilter(t)}
                  className={`px-2 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                    categoryFilter === t
                      ? 'bg-primary text-white'
                      : 'bg-surface text-dark-muted hover:bg-surface-dark/40 hover:text-dark'
                  }`}
                  title={`Show only moodboards tagged for ${PRODUCT_TYPE_LABEL[t]} (matches universal + legacy wearable for apparel/footwear/accessory)`}
                >
                  {PRODUCT_TYPE_LABEL[t]}
                </button>
              ))}
            </div>

            {/* Approval filter (Jul 7) — narrow to approved / unapproved so you
                can see + run the unapproved ones per category. */}
            <div className="mb-3 flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-dark-muted mr-1">
                Status:
              </span>
              {([
                { id: 'all', label: 'All' },
                { id: 'approved', label: '✓ Approved' },
                { id: 'unapproved', label: 'Unapproved' },
              ] as { id: 'all' | 'approved' | 'unapproved'; label: string }[]).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setApprovalFilter(opt.id)}
                  className={`px-2 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                    approvalFilter === opt.id
                      ? 'bg-primary text-white'
                      : 'bg-surface text-dark-muted hover:bg-surface-dark/40 hover:text-dark'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Category batch — render EVERY unapproved recipe in the picked
                category with the uploaded product; results stream into the
                Review list below to approve / deny / refine. */}
            {categoryFilter !== null && !selectMode && (
              <div className="mb-3 flex items-center gap-2 flex-wrap px-3 py-2.5 rounded-lg bg-primary/5 border border-primary/20">
                <span className="text-xs text-dark">
                  <strong>{unapprovedInCategoryCount}</strong> unapproved{' '}
                  {PRODUCT_TYPE_LABEL[categoryFilter]} recipe{unapprovedInCategoryCount === 1 ? '' : 's'}
                </span>
                <button
                  type="button"
                  onClick={() => void runUnapprovedInCategory()}
                  disabled={batchRunning || unapprovedInCategoryCount === 0 || !productPreview}
                  className="ml-auto px-4 py-1.5 rounded-full bg-primary text-white text-xs font-semibold disabled:opacity-40"
                  title={
                    !productPreview
                      ? 'Upload a product on the right first'
                      : `Render all ${unapprovedInCategoryCount} unapproved recipes with the uploaded product`
                  }
                >
                  {batchRunning
                    ? 'Rendering…'
                    : `▶ Render all ${unapprovedInCategoryCount} unapproved`}
                </button>
              </div>
            )}

            {libraryError && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs">{libraryError}</div>
            )}
            {!library && !libraryError && <div className="text-sm text-dark-muted">Loading…</div>}
            {library && (
              <div className="max-h-[520px] overflow-y-auto pr-1">
                {categoryFilter !== null && filteredTotal === 0 && (
                  <div className="px-3 py-6 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 text-center">
                    No moodboards tagged for <strong>{PRODUCT_TYPE_LABEL[categoryFilter]}</strong> yet.
                    {' '}Tag some on <a href="/moodboard-builder" className="underline">/moodboard-builder</a>, or{' '}
                    <button type="button" onClick={() => setCategoryFilter(null)} className="underline font-semibold">show all</button>.
                  </div>
                )}
                {VIBES.map((v) => {
                  const rawItems = library.groups[v] ?? [];
                  // Apply category filter — when set, drop tiles that
                  // don't match. Mirrors backend picker's filter logic so
                  // what's visible here is what production would consider.
                  const items = rawItems.filter(
                    (m) =>
                      matchesProductType(m, categoryFilter) &&
                      (approvalFilter === 'all' ||
                        (approvalFilter === 'approved' && m.ready_for_production) ||
                        (approvalFilter === 'unapproved' &&
                          !m.ready_for_production &&
                          !m.rejected)),
                  );
                  if (items.length === 0) return null;
                  const approvedCount = items.filter((m) => m.ready_for_production).length;
                  const rejectedCount = items.filter((m) => m.rejected).length;
                  const unreviewedCount = items.length - approvedCount - rejectedCount;
                  return (
                    <div key={v} className="mb-4">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-xs font-semibold uppercase tracking-wide text-dark-muted">
                          {v} <span className="text-dark-muted/60">({items.length})</span>
                        </span>
                        {approvedCount > 0 && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold border border-emerald-200">
                            <span className="w-1 h-1 rounded-full bg-emerald-500" />
                            {approvedCount} approved
                          </span>
                        )}
                        {rejectedCount > 0 && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 text-[10px] font-semibold border border-red-200">
                            <span className="w-1 h-1 rounded-full bg-red-500" />
                            {rejectedCount} rejected
                          </span>
                        )}
                        {unreviewedCount > 0 && (
                          <span className="text-[10px] text-dark-muted/70">
                            {unreviewedCount} unreviewed
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                        {items.map((m) => {
                          const isPicked = picked?.id === m.id;
                          const isSelected = selectedIds.has(m.id);
                          const isApproved = !!m.ready_for_production;
                          const isRejected = !!m.rejected;
                          // selectMode highlight: primary ring on chosen
                          // tiles; otherwise fall through to single-pick
                          // / approved / rejected / default styling.
                          const ringClass = selectMode && isSelected
                            ? 'border-primary ring-2 ring-primary/40 scale-[1.02]'
                            : isPicked
                              ? 'border-primary ring-2 ring-primary/30 scale-[1.02]'
                              : isApproved
                                ? 'border-emerald-300/80 ring-1 ring-emerald-200/60'
                                : isRejected
                                  ? 'border-red-300/80 ring-1 ring-red-200/60'
                                  : 'border-surface-dark/40 hover:border-dark-muted/60';
                          return (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() => selectMode ? toggleSelected(m.id) : setPicked(m)}
                              className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${ringClass}`}
                              title={
                                selectMode
                                  ? (isSelected ? 'Click to deselect' : 'Click to add to batch')
                                  : isApproved
                                    ? `Approved — production worker uses this recipe`
                                    : isRejected
                                      ? `Rejected — production worker skips this`
                                      : m.style_summary || m.id
                              }
                            >
                              <img
                                src={m.image_url}
                                alt={m.id}
                                className={`absolute inset-0 w-full h-full object-cover ${
                                  isRejected ? 'opacity-50 grayscale' : ''
                                }`}
                                loading="lazy"
                              />
                              {/* Status badge — top-left so the picked ✓ in
                                  the top-right doesn't overlap. */}
                              {isApproved && (
                                <div className="absolute top-1 left-1 inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-semibold shadow-sm">
                                  <span aria-hidden>✓</span>
                                </div>
                              )}
                              {isRejected && (
                                <div className="absolute top-1 left-1 inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-semibold shadow-sm">
                                  <span aria-hidden>✕</span>
                                </div>
                              )}
                              {/* Selected checkbox — large + on top of
                                  picked/approved badges so it's the
                                  obvious signal in multi-select mode. */}
                              {selectMode && isSelected && (
                                <div className="absolute top-1 right-1 w-6 h-6 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center shadow">
                                  ✓
                                </div>
                              )}
                              {!selectMode && isPicked && (
                                <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center">✓</div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {picked && (
              <div className="mt-3 px-3 py-2 rounded-lg bg-surface text-xs">
                <div className="font-semibold text-dark">{picked.id}</div>
                <div className="text-dark-muted mt-1 line-clamp-3">{picked.style_summary || '—'}</div>
              </div>
            )}
          </div>

          {/* RIGHT — Controls */}
          <div className="bg-white rounded-2xl border border-surface-dark/40 p-5 flex flex-col">
            <h2 className="font-semibold text-dark mb-4">2 — Product + models</h2>

            {/* Product input */}
            <div className="mb-4">
              <label className="block text-xs font-semibold uppercase tracking-wide text-dark-muted mb-1">
                Product
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  value={scrapeUrl}
                  onChange={(e) => setScrapeUrl(e.target.value)}
                  placeholder="Product page URL (scrape)"
                  className="flex-1 px-3 py-2 rounded-lg border border-surface-dark/50 text-sm"
                />
                <button
                  type="button"
                  onClick={() => void handleScrape()}
                  disabled={!scrapeUrl.trim() || scraping}
                  className="px-3 py-2 rounded-lg bg-surface-dark/40 text-dark text-sm disabled:opacity-40"
                >
                  {scraping ? 'Scraping…' : 'Scrape'}
                </button>
              </div>
              <div className="flex gap-2 items-center">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-2 rounded-lg bg-surface-dark/40 text-dark text-xs"
                >
                  Or upload file
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void acceptFile(f); e.target.value = ''; }}
                />
                {productPreview && (
                  <img src={productPreview} className="w-12 h-12 rounded object-contain bg-surface border border-surface-dark/40" alt="product" />
                )}
              </div>
              {scrapeError && (
                <div className="mt-2 px-3 py-1.5 rounded-lg bg-red-50 text-red-700 text-xs">{scrapeError}</div>
              )}
              <input
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder="Product name (for prompt)"
                className="mt-2 w-full px-3 py-2 rounded-lg border border-surface-dark/50 text-sm"
              />
              <input
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Brand name (anchors no-hallucination block — recommended)"
                className="mt-2 w-full px-3 py-2 rounded-lg border border-surface-dark/50 text-sm"
              />
            </div>

            {/* Vision model */}
            <div className="mb-3">
              <label className="block text-xs font-semibold uppercase tracking-wide text-dark-muted mb-1">
                Vision model
              </label>
              <select
                value={visionModel}
                onChange={(e) => setVisionModel(e.target.value as (typeof VISION_MODELS)[number])}
                className="w-full px-3 py-2 rounded-lg border border-surface-dark/50 text-sm bg-white"
              >
                {VISION_MODELS.map((m) => {
                  const meta = VISION_MODEL_META[m];
                  return (
                    <option key={m} value={m}>
                      {m} — {meta.cost} · {meta.latency}{meta.warn ? ' ⚠' : ''}
                    </option>
                  );
                })}
              </select>
              {VISION_MODEL_META[visionModel].warn && (
                <div className="mt-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs">
                  ⚠ <b>{visionModel}</b> is slow + expensive ({VISION_MODEL_META[visionModel].cost}/call, {VISION_MODEL_META[visionModel].latency}). Reasoning tokens bill even on timeout. Test the prompt + flow with a cheap model first.
                </div>
              )}
            </div>

            {/* Render model */}
            <div className="mb-3">
              <label className="block text-xs font-semibold uppercase tracking-wide text-dark-muted mb-1">
                Render model
              </label>
              <select
                value={renderModel}
                onChange={(e) => setRenderModel(e.target.value as (typeof RENDER_MODELS)[number])}
                className="w-full px-3 py-2 rounded-lg border border-surface-dark/50 text-sm bg-white"
              >
                {RENDER_MODELS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            {/* Style-ref mode */}
            <div className="mb-4">
              <label className="block text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">
                Style-ref mode
              </label>
              <div className="grid grid-cols-3 gap-2">
                {REF_MODES.map((m) => {
                  const active = m === refMode;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setRefMode(m)}
                      className={`px-2 py-2 rounded-lg text-xs font-medium border transition-colors ${
                        active
                          ? 'border-primary bg-primary-light text-primary'
                          : 'border-surface-dark/50 bg-white text-dark hover:border-dark-muted/60'
                      }`}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 text-xs text-dark-muted/70">
                Which inputs the renderer sees beyond the product:
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  <li><b>image-only</b>: moodboard image as ref (no text hints in prompt beyond layout)</li>
                  <li><b>image+text</b>: moodboard image AND layout-spec text in prompt</li>
                  <li><b>text-only</b>: NO moodboard image, just the layout-spec text</li>
                </ul>
              </div>
            </div>

            {/* Requires model — apparel/footwear/accessory case where the
                product is what the model wears/carries, not the model
                itself. Vision will extract a model block (body type, pose,
                framing, styling, mood) and the render prompt prepends a
                strict directive to render the model wearing/carrying the
                user's product. Leave off for skincare/consumable/tech. */}
            <div className="mb-4 rounded-lg border border-surface-dark/50 bg-white p-3">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requiresModel}
                  onChange={(e) => setRequiresModel(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-primary cursor-pointer"
                />
                <span className="flex-1">
                  <span className="block text-xs font-semibold uppercase tracking-wide text-dark">
                    Requires model
                  </span>
                  <span className="block text-xs text-dark-muted/80 mt-1 normal-case font-normal tracking-normal">
                    Vision extracts the model from the moodboard (body
                    type, pose, framing, styling, mood) and the render
                    forces a human-model composition with the product
                    being WORN or CARRIED. Use for apparel, footwear,
                    or accessory moodboards. Leave OFF for skincare,
                    consumable, tech, etc.
                  </span>
                </span>
              </label>
            </div>

            {runError && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs">{runError}</div>
            )}

            <button
              type="button"
              onClick={() => void handleRun()}
              disabled={!picked || !productPreview || running}
              className="mt-auto px-5 py-3 rounded-full bg-primary text-white font-medium text-sm disabled:opacity-40"
            >
              {running ? `Running… (vision + render, ~${visionModel.includes('pro') ? 25 : 15}s)` : 'Run smoke test'}
            </button>
          </div>
        </section>

        {/* Bottom: result */}
        {result && (
          <section className="bg-white rounded-2xl border border-surface-dark/40 p-5">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-semibold text-dark">3 — Result</h2>
              <div className="text-xs text-dark-muted">
                ${result.total_cost.toFixed(4)} · {result.total_latency_s}s · {result.style_ref_mode} · vision={result.vision_model} · render={result.render_model}
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Moodboard ref — show pasted if used, else library pick */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">
                  Moodboard ref {pastedPreview ? '(pasted)' : '(library)'}
                </div>
                <img
                  src={pastedPreview ?? picked?.image_url}
                  alt="moodboard"
                  className="w-full aspect-square object-cover rounded-lg border border-surface-dark/40"
                />
              </div>
              {/* Vision JSON */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">
                  Vision → JSON ({result.vision_model}, ${result.vision_cost.toFixed(4)}, {result.vision_latency_s}s)
                </div>
                <pre className="w-full aspect-square overflow-auto bg-surface rounded-lg border border-surface-dark/40 p-3 text-[11px] leading-tight text-dark">
                  {JSON.stringify(result.vision_json, null, 2)}
                </pre>
              </div>
              {/* Rendered ad */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">
                  Rendered ad ({result.render_model}, ${result.render_cost.toFixed(4)}, {result.render_latency_s}s)
                </div>
                <img
                  src={`data:image/png;base64,${result.render_image_b64}`}
                  alt="render"
                  className="w-full aspect-square object-contain rounded-lg border border-surface-dark/40 bg-white"
                />
              </div>
            </div>
            {/* Palette harmonization callout — surfaces what vision shifted */}
            {(() => {
              const palette = (result.vision_json as Record<string, unknown>).palette as
                | Record<string, unknown>
                | undefined;
              if (!palette) return null;
              const original = String(palette.moodboard_original_palette ?? '').trim();
              const harmonized = String(palette.all_colors ?? '').trim();
              const note = String(palette.harmonization_note ?? '').trim();
              if (!note && !original) return null;
              return (
                <div className="mt-4 px-4 py-3 rounded-lg bg-amber-50/60 border border-amber-200 text-xs text-amber-900">
                  <div className="font-semibold uppercase tracking-wide mb-1">🎨 Palette harmonization</div>
                  {original && (
                    <div><b>Moodboard original:</b> {original}</div>
                  )}
                  {harmonized && (
                    <div><b>Harmonized (used in render):</b> {harmonized}</div>
                  )}
                  {note && <div className="mt-1 italic">{note}</div>}
                </div>
              );
            })()}
            {/* Render prompt collapsible */}
            <details className="mt-4">
              <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-dark-muted hover:text-dark">
                Render prompt (click to expand)
              </summary>
              <pre className="mt-2 px-3 py-2 bg-surface rounded-lg border border-surface-dark/40 text-[11px] text-dark whitespace-pre-wrap">{result.render_prompt}</pre>
            </details>

            {/* ─── APPROVE this combo ─── */}
            <div className="mt-5 px-4 py-3 rounded-lg bg-emerald-50/60 border border-emerald-200">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-emerald-900">
                  ✓ Approve this combination
                </div>
                <div className="text-[11px] text-emerald-800/70">
                  Persists to backend/scripts/smoke-test-approved.json — used to import the approved combo into the production app later.
                </div>
              </div>
              <div className="flex gap-2">
                <input
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder="Optional note (why this works, what to remember)"
                  className="flex-1 px-3 py-2 rounded-lg border border-emerald-300 text-sm bg-white"
                />
                <button
                  type="button"
                  onClick={() => void handleApprove()}
                  disabled={approving}
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white font-medium text-sm disabled:opacity-40 hover:bg-emerald-700"
                >
                  {approving ? 'Saving…' : '✓ Approve'}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ─── BATCH ACTION BAR (Jun 23) — only in select mode ───────
            Renders below the single-result section. Shows N selected,
            an estimated cost, the Run All button, and a Cancel control
            during runs. Cost is rough — ~$0.05/render (vision $0.02 +
            render $0.01-0.04). 20 selected = ~$1. */}
        {(selectMode || batchRunning || batchRuns.size > 0) && (
          <section className="mt-6 bg-white rounded-2xl border-2 border-primary/40 p-5 sticky bottom-4 shadow-lg">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-baseline gap-3">
                <h2 className="font-bold text-dark text-lg">
                  {selectMode ? 'Batch render' : 'Review'}
                </h2>
                <span className="text-sm text-dark-muted">
                  {selectMode
                    ? (selectedIds.size === 0
                        ? 'No moodboards selected yet — click tiles to add them.'
                        : `${selectedIds.size} selected · est. ~$${(selectedIds.size * 0.05).toFixed(2)}`)
                    : `${Array.from(batchRuns.values()).filter((r) => r.status === 'done' || r.status === 'approved').length}/${batchRuns.size} rendered — approve, deny, or refine each`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {batchRunning ? (
                  <button
                    type="button"
                    onClick={() => { batchCancelRef.current = true; }}
                    className="px-4 py-2 rounded-full bg-red-50 text-red-700 border border-red-200 text-sm font-semibold hover:bg-red-100"
                  >Cancel (in-flight finish)</button>
                ) : selectMode ? (
                  <button
                    type="button"
                    onClick={() => void runBatch()}
                    disabled={selectedIds.size === 0 || !productPreview || batchRunning}
                    className="px-5 py-2 rounded-full bg-primary text-white font-semibold text-sm disabled:opacity-40"
                    title={
                      !productPreview
                        ? 'Pick a product on the right first'
                        : `Render all ${selectedIds.size} moodboards in parallel (${BATCH_CONCURRENCY} at a time)`
                    }
                  >
                    {selectedIds.size > 0
                      ? `Run all ${selectedIds.size}`
                      : 'Run all'}
                  </button>
                ) : null}
                {/* Close the batch panel. In multi-select it exits select
                    mode + clears selection + results; in the category-batch
                    review it just clears the results list. */}
                <button
                  type="button"
                  onClick={() => {
                    if (selectMode) exitSelectMode();
                    else setBatchRuns(new Map());
                  }}
                  disabled={batchRunning}
                  className="w-9 h-9 rounded-full text-dark-muted hover:text-dark hover:bg-surface text-xl leading-none flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                  title={
                    batchRunning
                      ? 'Cancel the running batch first'
                      : selectMode ? 'Close batch panel + exit multi-select' : 'Clear the review list'
                  }
                  aria-label="Close batch panel"
                >×</button>
              </div>
            </div>

            {/* Review list — streams in as each render completes.
                Per-tile Approve / Deny + an edit-to-remove → refine step. */}
            {batchRuns.size > 0 && (
              <div className="mt-5 pt-5 border-t border-surface-dark/40">
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="font-semibold text-dark">
                    Results · {Array.from(batchRuns.values()).filter((r) => r.status === 'done' || r.status === 'approved').length}/{batchRuns.size} rendered
                  </h3>
                  <span className="text-xs text-dark-muted">
                    Approved: {Array.from(batchRuns.values()).filter((r) => r.status === 'approved').length}
                    {' · '}
                    Errored: {Array.from(batchRuns.values()).filter((r) => r.status === 'error').length}
                  </span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {Array.from(batchRuns.values()).map((entry) => {
                    const { moodboard: m, status, result: r, error } = entry;
                    return (
                      <div
                        key={m.id}
                        className={`rounded-xl border-2 overflow-hidden bg-white ${
                          status === 'approved'
                            ? 'border-emerald-400 ring-2 ring-emerald-100'
                            : status === 'discarded'
                              ? 'border-surface-dark/40 opacity-50'
                              : status === 'error'
                                ? 'border-red-300 ring-1 ring-red-100'
                                : 'border-surface-dark/40'
                        }`}
                      >
                        <div className="grid grid-cols-2 gap-0">
                          {/* Source moodboard ref — left */}
                          <div className="relative aspect-square overflow-hidden border-r border-surface-dark/30">
                            <img
                              src={m.image_url}
                              alt={m.id}
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                            <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px] font-mono">
                              SOURCE
                            </span>
                          </div>
                          {/* Render result — right */}
                          <div className="relative aspect-square overflow-hidden bg-surface flex items-center justify-center">
                            {status === 'queued' && (
                              <span className="text-xs text-dark-muted">Queued…</span>
                            )}
                            {status === 'running' && (
                              <div className="flex flex-col items-center gap-1.5">
                                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                <span className="text-[10px] text-dark-muted">Rendering…</span>
                              </div>
                            )}
                            {status === 'error' && (
                              <div className="text-[10px] text-red-700 p-2 text-center">
                                {error || 'Failed'}
                              </div>
                            )}
                            {(status === 'done' || status === 'approved' || status === 'discarded') && r && (
                              <>
                                <img
                                  src={`data:image/png;base64,${r.render_image_b64}`}
                                  alt={`render of ${m.id}`}
                                  className="absolute inset-0 w-full h-full object-cover"
                                />
                                <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px] font-mono">
                                  RENDER
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        {/* Footer — id + primary actions */}
                        <div className="px-2.5 py-2 border-t border-surface-dark/30 flex items-center justify-between gap-2">
                          <div className="text-[10px] font-mono text-dark-muted truncate">
                            …{m.id.slice(-10)}
                          </div>
                          <div className="flex items-center gap-1">
                            {status === 'done' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => void approveBatchOne(m.id)}
                                  disabled={!!entry.refining}
                                  className="px-2.5 py-1 rounded-md bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 disabled:opacity-40"
                                >Approve</button>
                                <button
                                  type="button"
                                  onClick={() => void rejectBatchOne(m.id)}
                                  disabled={!!entry.refining}
                                  className="px-2.5 py-1 rounded-md bg-red-50 text-red-700 border border-red-200 text-xs font-semibold hover:bg-red-100 disabled:opacity-40"
                                  title="Reject this recipe — removes it from the list and marks it rejected (excluded from production)"
                                >Deny</button>
                              </>
                            )}
                            {status === 'approved' && (
                              <span className="text-xs font-semibold text-emerald-700">✓ Approved</span>
                            )}
                            {status === 'error' && (
                              <button
                                type="button"
                                onClick={() => setBatchRuns((prev) => {
                                  const next = new Map(prev);
                                  next.delete(m.id);
                                  return next;
                                })}
                                className="px-2 py-1 rounded-md bg-surface text-dark-muted text-xs hover:bg-surface-dark/40"
                              >Dismiss</button>
                            )}
                          </div>
                        </div>
                        {/* Edit-to-remove → refine. When the render came back
                            too close to the moodboard (identical props), name
                            what to strip; Update preview cleans the recipe +
                            re-renders in place, then Approve saves the clean one. */}
                        {status === 'done' && (
                          <div className="px-2.5 pb-2.5 flex items-center gap-1.5">
                            <input
                              type="text"
                              value={entry.removeText ?? ''}
                              onChange={(e) => setBatchRemoveText(m.id, e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') void refineBatchOne(m.id); }}
                              placeholder="Remove props/elements — e.g. dried flowers, citrus"
                              disabled={!!entry.refining}
                              className="flex-1 min-w-0 text-[11px] px-2 py-1 border border-surface-dark/40 rounded disabled:opacity-50"
                            />
                            <button
                              type="button"
                              onClick={() => void refineBatchOne(m.id)}
                              disabled={!!entry.refining || !(entry.removeText ?? '').trim()}
                              className="px-2.5 py-1 rounded-md bg-dark text-white text-[11px] font-semibold disabled:opacity-40 whitespace-nowrap"
                            >{entry.refining ? 'Updating…' : 'Update preview'}</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
